// client/src/components/CategoryBar.js
// Secondary navigation row under the main navbar:
//   [All categories] [New Arrivals] [Specials] | first few categories
// "All categories" opens a two-column panel: categories on the left, the hovered
// category's subcategories on the right.
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box, Button, Divider, List, ListItemButton, ListItemText, Popover, Stack, Typography,
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import MenuIcon from '@mui/icons-material/Menu';
import { useCategories } from '../context/CategoryContext';
import { getCategoryIcon } from '../constants/categoryIcons';

const QUICK_CATEGORY_COUNT = 4;

const CategoryBar = () => {
  const navigate = useNavigate();
  const { categories } = useCategories();
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const activeCategory = categories.find((c) => c.id === activeId) || categories[0];
  const quickCategories = categories.filter((c) => c.name !== 'Other').slice(0, QUICK_CATEGORY_COUNT);

  const closeMenu = () => setAnchorEl(null);

  const goTo = (params) => {
    closeMenu();
    navigate(`/?${new URLSearchParams(params).toString()}`);
  };

  const linkSx = { color: 'text.primary', fontWeight: 600, px: 1.5, whiteSpace: 'nowrap', flexShrink: 0 };

  return (
    <Box sx={{ borderTop: 1, borderColor: 'divider' }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={0.25}
        sx={{ px: { xs: 1, sm: 2 }, minHeight: 42, overflowX: 'auto', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
      >
        <Button
          size="small"
          color="inherit"
          startIcon={<MenuIcon fontSize="small" />}
          onClick={(e) => { setActiveId(categories[0]?.id ?? null); setAnchorEl(e.currentTarget); }}
          sx={{ ...linkSx, fontWeight: 700 }}
        >
          All categories
        </Button>
        <Button size="small" color="inherit" onClick={() => goTo({ sort: 'newest' })} sx={linkSx}>New Arrivals</Button>
        <Button size="small" onClick={() => goTo({ sale: 'true' })} sx={{ ...linkSx, color: 'error.main' }}>Specials</Button>
        <Button size="small" color="inherit" component={Link} to="/beginner-guide" sx={linkSx}>Beginner's Guide</Button>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 1.25, display: { xs: 'none', md: 'block' } }} />
        {quickCategories.map((category) => (
          <Button
            key={category.id}
            size="small"
            color="inherit"
            onClick={() => goTo({ category: category.name })}
            sx={{ ...linkSx, fontWeight: 500, color: 'text.secondary', display: { xs: 'none', md: 'inline-flex' } }}
          >
            {category.name}
          </Button>
        ))}
      </Stack>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { mt: 0.5, overflow: 'hidden' } } }}
      >
        {categories.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 2.5 }}>No categories yet.</Typography>
        ) : (
          <Stack direction="row" sx={{ width: 580, maxWidth: '92vw', maxHeight: '70vh' }}>
            <Box sx={{ width: '48%', py: 1, overflowY: 'auto', borderRight: 1, borderColor: 'divider' }}>
              <List dense disablePadding>
                {categories.map((category) => {
                  const Icon = getCategoryIcon(category.icon_key);
                  return (
                    <ListItemButton
                      key={category.id}
                      selected={activeCategory?.id === category.id}
                      onMouseEnter={() => setActiveId(category.id)}
                      onFocus={() => setActiveId(category.id)}
                      onClick={() => goTo({ category: category.name })}
                    >
                      <Icon color="primary" fontSize="small" sx={{ mr: 1.5 }} />
                      <ListItemText primary={category.name} />
                      {category.children.length > 0 && <ChevronRightIcon fontSize="small" color="disabled" />}
                    </ListItemButton>
                  );
                })}
              </List>
            </Box>
            <Box sx={{ width: '52%', py: 1.5, px: 1.5, overflowY: 'auto' }}>
              <Typography variant="overline" color="text.secondary" sx={{ px: 1 }}>{activeCategory?.name}</Typography>
              <List dense disablePadding>
                <ListItemButton onClick={() => goTo({ category: activeCategory?.name })}>
                  <ListItemText primary={`All ${activeCategory?.name}`} primaryTypographyProps={{ fontWeight: 700 }} />
                </ListItemButton>
                {activeCategory?.children.map((sub) => (
                  <ListItemButton key={sub.id} onClick={() => goTo({ category: activeCategory.name, subcategory: sub.name })}>
                    <ListItemText primary={sub.name} />
                  </ListItemButton>
                ))}
              </List>
            </Box>
          </Stack>
        )}
      </Popover>
    </Box>
  );
};

export default CategoryBar;
